import {
    Alert,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { useLoaderData, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";
import JalaliDatePicker from "../../../../components/shared/jalali-datetimepicker";
import { toJalaliFullDate, formatLocalDateTimeForApi } from "../../../../middleware/middleware";

const hasValidVisitDate = (value) => {
  if (!value) return false;
  if (typeof value === "string" && value.startsWith("0001-01-01")) return false;
  if (value instanceof Date && value.getFullYear() <= 1) return false;
  return true;
};

/** Dropdown values are enum names ("SinglePhase"); API expects 1 / 3. */
const resolvePhaseCountNumber = (value) => {
  if (value == null || value === "") return null;
  if (value === "SinglePhase" || value === 1 || value === "1") return 1;
  if (value === "ThreePhase" || value === 3 || value === "3") return 3;
  const n = Number(value);
  if (n === 1 || n === 3) return n;
  return null;
};

const toPhaseCountFormValue = (value) => {
  const n = resolvePhaseCountNumber(value);
  if (n === 3) return "ThreePhase";
  return "SinglePhase"; // default تک‌فاز
};

const INSTALLATION_LOCATION_OTHER = "Other";

const resolveInstallationLocationType = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number") {
    const map = {
      1: "FlatRoof",
      2: "PitchedRoof",
      3: "Ground",
      4: "Canopy",
      9: "Other",
    };
    return map[value] || "";
  }
  const s = String(value).trim();
  const allowed = ["FlatRoof", "PitchedRoof", "Ground", "Canopy", "Other"];
  if (allowed.includes(s)) return s;
  if (s === "1") return "FlatRoof";
  if (s === "2") return "PitchedRoof";
  if (s === "3") return "Ground";
  if (s === "4") return "Canopy";
  if (s === "9") return "Other";
  return "";
};

const toInstallationLocationTypeNumber = (value) => {
  const key = resolveInstallationLocationType(value);
  const map = {
    FlatRoof: 1,
    PitchedRoof: 2,
    Ground: 3,
    Canopy: 4,
    Other: 9,
  };
  return map[key] ?? null;
};

const validationSchema = Yup.object().shape({
  actualVisitTime: Yup.mixed().test(
    "required-actual",
    "زمان انجام بازدید الزامی است.",
    (value) => hasValidVisitDate(value)
  ),
  proposedCapacity: Yup.number()
    .typeError("ظرفیت پیشنهادی باید عدد باشد.")
    .moreThan(0, "ظرفیت پیشنهادی پس از بازدید الزامی است."),
  phaseCount: Yup.mixed()
    .required("نوع انشعاب را انتخاب کنید.")
    .test(
      "phase-required",
      "نوع انشعاب را انتخاب کنید (تک‌فاز / سه‌فاز).",
      (value) => resolvePhaseCountNumber(value) != null
    ),
  installationLocationType: Yup.string()
    .required("نوع محل نصب را انتخاب کنید.")
    .oneOf(
      ["FlatRoof", "PitchedRoof", "Ground", "Canopy", "Other"],
      "نوع محل نصب نامعتبر است."
    ),
  installationLocationOther: Yup.string().when("installationLocationType", {
    is: INSTALLATION_LOCATION_OTHER,
    then: () =>
      Yup.string()
        .trim()
        .required("برای گزینه سایر، توضیح محل نصب را وارد کنید.")
        .max(200, "توضیح حداکثر ۲۰۰ کاراکتر است."),
    otherwise: () => Yup.string().nullable(),
  }),
  siteVisitStatus: Yup.mixed().required("وضعیت بازدید را انتخاب کنید."),
});

const normalizeSiteVisitForForm = (raw) => {
  const v = raw || {};
  const normalized = { ...v };

  // show placeholders instead of misleading zeroes
  ["proposedCapacity", "amper", "requestedCapacity", "transformerCapacity", "distanceFromTransformer"].forEach((k) => {
    if (normalized[k] === 0) normalized[k] = "";
  });
  normalized.phaseCount = toPhaseCountFormValue(
    normalized.phaseCount ?? normalized.PhaseCount
  );
  normalized.installationLocationType = resolveInstallationLocationType(
    normalized.installationLocationType ?? normalized.InstallationLocationType
  );
  normalized.installationLocationOther =
    normalized.installationLocationOther ??
    normalized.InstallationLocationOther ??
    "";

  // backend default (0001-01-01) should be treated as empty in UI
  ["scheduledVisitTime", "actualVisitTime"].forEach((k) => {
    const val = normalized[k];
    if (typeof val === "string" && val.startsWith("0001-01-01")) normalized[k] = "";
  });

  // شناسه قبض از درخواست (API روی BillId می‌گذارد)
  normalized.billId = normalized.billId || normalized.BillId || "";

  return normalized;
};

const SiteVisitModifyPage = () => {
  const data = useLoaderData("root") || {};
  const siteVisit = data.siteVisit || data.SiteVisit || {};
  const initialValues = normalizeSiteVisitForForm(siteVisit);
  const requestBillId = initialValues.billId || "";
  const phaseCount = data.phaseCount || data.PhaseCount || [];
  const installationLocationTypes =
    data.installationLocationTypes || data.InstallationLocationTypes || [];
  const siteVisitStatus = data.siteVisitStatus || data.SiteVisitStatus || [];
  const siteVisitTitle =
    siteVisit.requestTitle ||
    siteVisit.RequestTitle ||
    siteVisit.title ||
    siteVisit.Title ||
    "";
  const scheduledTimeSet = hasValidVisitDate(siteVisit.scheduledVisitTime);
  const scheduledTimeLabel = scheduledTimeSet
    ? toJalaliFullDate(siteVisit.scheduledVisitTime)
    : "هنوز هماهنگ نشده";
  let navigate = useNavigate();
  const location = useLocation();
  const { id: roleId } = useParams();

  const [loading, setLoading] = useState(false);

  const onNavigateToListPage = () => {
    const from = location.state?.from;
    if (
      typeof from === "string" &&
      from.startsWith("/SLN/Contractor") &&
      !from.includes("/SiteVisit/")
    ) {
      navigate(from);
      return;
    }
    navigate("/SLN/Contractor/ProjectAssignments");
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const toIntOrZero = (value) => {
      if (value == null || value === "") return 0;
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n : 0;
    };
    const toNumberOrZero = (value) => {
      if (value == null || value === "") return 0;
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n : 0;
    };

    const sendData = {
      ...siteVisit,
      ...formValue,
      id: siteVisit.id || formValue.id,
      billId: requestBillId || formValue.billId || siteVisit.billId || "",
      // Backend expects non-null Int32; UI may hold ""/null for placeholders.
      requestedCapacity: toIntOrZero(
        formValue.requestedCapacity ?? siteVisit.requestedCapacity
      ),
      proposedCapacity: toIntOrZero(
        formValue.proposedCapacity ?? siteVisit.proposedCapacity
      ),
      amper: toIntOrZero(formValue.amper ?? siteVisit.amper),
      transformerCapacity: toNumberOrZero(
        formValue.transformerCapacity ?? siteVisit.transformerCapacity
      ),
      distanceFromTransformer: toNumberOrZero(
        formValue.distanceFromTransformer ?? siteVisit.distanceFromTransformer
      ),
      phaseCount:
        resolvePhaseCountNumber(formValue.phaseCount ?? siteVisit.phaseCount) ?? 1,
      installationLocationType: resolveInstallationLocationType(
        formValue.installationLocationType
      ),
      InstallationLocationType: toInstallationLocationTypeNumber(
        formValue.installationLocationType
      ),
      installationLocationOther:
        resolveInstallationLocationType(formValue.installationLocationType) ===
        INSTALLATION_LOCATION_OTHER
          ? String(formValue.installationLocationOther || "").trim()
          : null,
      InstallationLocationOther:
        resolveInstallationLocationType(formValue.installationLocationType) ===
        INSTALLATION_LOCATION_OTHER
          ? String(formValue.installationLocationOther || "").trim()
          : null,
      scheduledVisitTime: formatLocalDateTimeForApi(
        formValue.scheduledVisitTime ?? siteVisit.scheduledVisitTime
      ),
      actualVisitTime: formatLocalDateTimeForApi(
        formValue.actualVisitTime ?? siteVisit.actualVisitTime
      ),
    };

    try {
      const res = await DefaultApiService.modifyApi(
        "SLN/Contractor/SiteVisit",
        sendData
      );

      if (res.isSuccess) {
        toast.success(res.message);
        onNavigateToListPage();
      } else {
        toast.error(res.message);
      }

      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");

      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              roleId !== "create"
                ? `ثبت اطلاعات بازدید — ${siteVisitTitle || "بازدید"}`
                : "ایجاد"
            }
            subheader="زمان هماهنگ‌شده از قبل ثبت شده است؛ در این فرم فقط نتیجه و جزئیات بازدید را وارد کنید."
          />

          <CardContent>
            {!scheduledTimeSet ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  زمان بازدید هنوز هماهنگ نشده است. ابتدا از{" "}
                  <strong>لیست بازدید</strong> (آیکن تعیین زمان) زمان
                  هماهنگ‌شده را ثبت کنید، سپس به این فرم برگردید.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onNavigateToListPage}
                >
                  بازگشت به لیست بازدید
                </Button>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                زمان هماهنگ‌شده: <strong>{scheduledTimeLabel}</strong>
              </Alert>
            )}

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name, placeholder = "") => ({
                  name: name,
                  fullWidth: true,
                  value: values[name] ?? "",
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                  placeholder,
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ظرفیت درخواستی (کیلووات)"
                          disabled={true}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "requestedCapacity",
                            "ظرفیت درخواستی مشتری (کیلووات)"
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ظرفیت پیشنهادی (کیلووات)"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "proposedCapacity",
                            "ظرفیت پیشنهادی پس از بازدید (کیلووات)"
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="آمپر"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("amper", "مقدار آمپر انشعاب، مثلاً ۲۵")}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ظرفیت ترانس (kVA)"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "transformerCapacity",
                            "مثلاً ۲۰۰"
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="فاصله از ترانس (متر)"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "distanceFromTransformer",
                            "مثلاً ۵۰"
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="شناسه قبض"
                          disabled
                          value={requestBillId || values.billId || "—"}
                          helperText="از اطلاعات درخواست (فقط نمایش)"
                          fullWidth
                          variant="outlined"
                          inputProps={{ dir: "ltr" }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="رمز سامانه"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "computerPassword",
                            "رمز کامپیوتر یا سامانه را وارد کنید"
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="زمان هماهنگ‌شده بازدید"
                          disabled
                          value={scheduledTimeLabel}
                          helperText="از لیست بازدید ثبت شده؛ در این فرم قابل تغییر نیست"
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <JalaliDatePicker
                          label="زمان انجام بازدید"
                          disabled={!scheduledTimeSet}
                          onChange={(value) => {
                            setFieldValue("actualVisitTime", value);
                          }}
                          {...onMakeFormikVariable(
                            "actualVisitTime",
                            "تاریخ و ساعت واقعی انجام بازدید"
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label="نوع انشعاب *"
                          displayEmpty
                          renderValue={(selected) => {
                            if (
                              selected === "" ||
                              selected === null ||
                              selected === undefined
                            ) {
                              return (
                                <span style={{ color: "rgba(0, 0, 0, 0.42)" }}>
                                  تک‌فاز یا سه‌فاز را انتخاب کنید
                                </span>
                              );
                            }
                            const item = phaseCount.find(
                              (x) => String(x.value) === String(selected)
                            );
                            return item?.name || selected;
                          }}
                          onChange={(event) => {
                            setFieldValue("phaseCount", event.target.value);
                          }}
                          list={phaseCount}
                          filterable={false}
                          name="phaseCount"
                          value={values.phaseCount ?? ""}
                          error={errors.phaseCount && touched.phaseCount}
                          helperText={
                            errors.phaseCount &&
                            touched.phaseCount &&
                            errors.phaseCount
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label="نوع محل نصب پنل *"
                          displayEmpty
                          onChange={(event) => {
                            const next = event.target.value;
                            setFieldValue("installationLocationType", next);
                            if (next !== INSTALLATION_LOCATION_OTHER) {
                              setFieldValue("installationLocationOther", "");
                            }
                          }}
                          list={installationLocationTypes}
                          filterable={false}
                          name="installationLocationType"
                          value={values.installationLocationType ?? ""}
                          error={
                            errors.installationLocationType &&
                            touched.installationLocationType
                          }
                          helperText={
                            errors.installationLocationType &&
                            touched.installationLocationType &&
                            errors.installationLocationType
                          }
                        />
                      </Grid>

                      {values.installationLocationType ===
                      INSTALLATION_LOCATION_OTHER ? (
                        <Grid item xs={12} md={6} lg={6}>
                          <TextField
                            label="توضیح محل نصب (سایر) *"
                            onChange={wrapFormikHandleChange(handleChange)}
                            {...onMakeFormikVariable("installationLocationOther")}
                          />
                        </Grid>
                      ) : null}

                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          name="isSharedRooftop"
                          value={values.isSharedRooftop}
                          checked={values.isSharedRooftop}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="محل نصب سامانه، پشت‌بام یا فضای مشاع ساختمان است"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          name="hasMeterLocation"
                          value={values.hasMeterLocation}
                          checked={values.hasMeterLocation}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="محل مناسب جهت نصب کنتور هوشمند موجود می باشد"
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          name="isBuildingUnder10Yrs"
                          value={values.isBuildingUnder10Yrs}
                          checked={values.isBuildingUnder10Yrs}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="ساختمان مشترک دارای عمری کمتر از 10 سال می باشد
"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          name="isFullSunlight"
                          value={values.isFullSunlight}
                          checked={values.isFullSunlight}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="محل طی روز آفتاب گیر بوده و هیچ مانعی بر روی آرایه منصوبه سایه ایجاد نمی کند
"
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        <FormControlLabel
                          name="isSubscriptionVerified"
                          value={values.isSubscriptionVerified}
                          checked={values.isSubscriptionVerified}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="مشخصات اشتراک به طور کامل درج و با قبض مشترک مطابقت دارد
"
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <FormControlLabel
                          name="isVisitedByDistributionCompanySupervisor"
                          value={values.isVisitedByDistributionCompanySupervisor}
                          checked={Boolean(values.isVisitedByDistributionCompanySupervisor)}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="بازدید توسط ناظر شرکت توزیع انجام شده است (نظارت توسط اینجانب انجام نشده)"
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={6}>
                        <TextField
                          label="توضیحات"
                          multiline
                          minRows={2}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable(
                            "description",
                            "توضیحات تکمیلی بازدید را وارد کنید"
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label="وضعیت"
                          displayEmpty
                          renderValue={(selected) => {
                            if (
                              selected === "" ||
                              selected === null ||
                              selected === undefined
                            ) {
                              return (
                                <span style={{ color: "rgba(0, 0, 0, 0.42)" }}>
                                  وضعیت بازدید را انتخاب کنید
                                </span>
                              );
                            }
                            const item = siteVisitStatus.find(
                              (x) => String(x.value) === String(selected)
                            );
                            return item?.name || selected;
                          }}
                          onChange={(event) => {
                            setFieldValue(
                              "siteVisitStatus",
                              event.target.value
                            );
                          }}
                          list={siteVisitStatus}
                          filterable={false}
                          name="siteVisitStatus"
                          value={values.siteVisitStatus ?? ""}
                          error={errors.siteVisitStatus && touched.siteVisitStatus}
                          helperText={
                            errors.siteVisitStatus &&
                            touched.siteVisitStatus &&
                            errors.siteVisitStatus
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent={"flex-end"}
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={isSubmitting || !scheduledTimeSet}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {roleId !== "create" ? "ثبت اطلاعات بازدید" : "ایجاد"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SiteVisitModifyPage;

